import React, { useEffect, useState, useRef, useMemo, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import {
  Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, Tab,
  CircularProgress, TextField, Button,
  Chip, FormControl, Select, MenuItem, Alert, Snackbar, InputLabel, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  useTheme, useMediaQuery, Collapse, IconButton, GlobalStyles, Grid,
  Switch, FormControlLabel, Popover, FormGroup, List, ListItem, ListItemIcon, ListItemText, Menu
} from '@mui/material';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SyncIcon from '@mui/icons-material/Sync';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import InputAdornment from '@mui/material/InputAdornment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import DensityLargeIcon from '@mui/icons-material/DensityLarge';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';

const API_BASE_URL = window.location.hostname === 'localhost' ? 'http://localhost:5000' : '';

const formatSchemeName = (name) => {
  if (!name) return '';
  return name
    .replace(/\s*-?\s*regular\s*plan\s*-?\s*growth/ig, '')
    .replace(/\s*-?\s*regular\s*plan/ig, '')
    .replace(/\s*-?\s*regular\s*growth/ig, '')
    .replace(/\s*-?\s*direct\s*plan\s*-?\s*growth/ig, '')
    .replace(/\s*-?\s*direct\s*plan/ig, '')
    .replace(/\s*-?\s*growth\s*option/ig, '')
    .replace(/\s*-?\s*growth/ig, '')
    .replace(/\s*-?\s*regular/ig, '')
    .replace(/\s*-?\s*direct/ig, '')
    .replace(/\s*-?\s*idcw/ig, '')
    .replace(/-+$/, '')
    .trim();
};

const getVal = (val) => (val && val !== '-' && !isNaN(parseFloat(val)) ? parseFloat(val) : null);

const menuPaperProps = {
  PaperProps: {
    sx: {
      bgcolor: '#ffffff', backgroundImage: 'none', mt: 0.5, borderRadius: 2, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)',
      '& .MuiList-root': { p: 1 },
      '& .MuiMenuItem-root:hover': { color: '#000000 !important' },
      '& .MuiMenuItem-root.Mui-selected:hover': { color: '#000000 !important' },
      '& .MuiMenuItem-root.Mui-selected': { color: '#000000 !important' }
    }
  }
};

const FundSelector = () => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [funds, setFunds] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('All');
  const [subCategory, setSubCategory] = useState('All');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [topFundsDialogOpen, setTopFundsDialogOpen] = useState(false);
  const [topFundsData, setTopFundsData] = useState({ averages: null, top5: [] });
  const [loadingTopFunds, setLoadingTopFunds] = useState(false);

  const [formulaDialogOpen, setFormulaDialogOpen] = useState(false);
  const [confirmFormulaDialog, setConfirmFormulaDialog] = useState(false);
  const [formulas, setFormulas] = useState({});
  const [formulasLoaded, setFormulasLoaded] = useState(false);
  const formRef = useRef(null);

  const [mobFormulaCat, setMobFormulaCat] = useState('');
  const [mobFormulaSubCat, setMobFormulaSubCat] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedFundDetails, setSelectedFundDetails] = useState(null);

  // Table Setting States
  const baseColumns = useMemo(() => [
    { field: 'name', headerName: 'Scheme Name', defaultWidth: 320 },
    { field: 'benchmark', headerName: 'Benchmark', defaultWidth: 220 },
    { field: 'code', headerName: 'Code', defaultWidth: 100 },
    { field: 'category', headerName: 'Category', defaultWidth: 140 },
    { field: 'subCategory', headerName: 'Sub Category', defaultWidth: 180 },
    { field: 'aum', headerName: 'AUM (Cr)', defaultWidth: 110, align: 'right' },
    { field: 'nav', headerName: 'NAV', defaultWidth: 100, align: 'right' },
    { field: 'navDate', headerName: 'Date', defaultWidth: 110, align: 'center' },
    { field: 'returnYTD', headerName: 'YTD (%)', defaultWidth: 110, align: 'center' },
    { field: 'return1y', headerName: '1Y (%)', defaultWidth: 110, align: 'center' },
    { field: 'return2y', headerName: '2Y (%)', defaultWidth: 110, align: 'center' },
    { field: 'return3y', headerName: '3Y (%)', defaultWidth: 110, align: 'center' },
    { field: 'return5y', headerName: '5Y (%)', defaultWidth: 110, align: 'center' },
    { field: 'return10y', headerName: '10Y (%)', defaultWidth: 110, align: 'center' },
    { field: 'returnInception', headerName: 'Inception (%)', defaultWidth: 120, align: 'center' },
    { field: 'dateInception', headerName: 'Inception Date', defaultWidth: 130, align: 'center' }
  ], []);

  const [colOrder, setColOrder] = useState(() => JSON.parse(localStorage.getItem('fsColOrder')) || baseColumns.map(c => c.field));
  const [colWidths, setColWidths] = useState(() => JSON.parse(localStorage.getItem('fsColWidths')) || {});
  const [frozenUntil, setFrozenUntil] = useState(() => localStorage.getItem('fsFrozenUntil') || 'name');
  const [hiddenColumns, setHiddenColumns] = useState(() => JSON.parse(localStorage.getItem('fsHiddenCols')) || []);

  const [colAnchorEl, setColAnchorEl] = useState(null);
  const [colSearch, setColSearch] = useState('');
  const [densityAnchorEl, setDensityAnchorEl] = useState(null);
  const [tableDensity, setTableDensity] = useState('compact');

  const [hoveredCol, setHoveredCol] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [headerMenuAnchorEl, setHeaderMenuAnchorEl] = useState(null);
  const [activeHeaderMenuCol, setActiveHeaderMenuCol] = useState(null);
  const [activeHeaderElement, setActiveHeaderElement] = useState(null);

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [colFilters, setColFilters] = useState({});
  const [currentFilter, setCurrentFilter] = useState({ field: '', operator: 'contains', value: '' });

  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSystemOwner = storedUser.role === 'System Owner';
  const userPermissions = storedUser.permissions || {};
  const hasTop5Permission = isSystemOwner || userPermissions.top5Fund === true;

  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const getUploadHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' } });

  useEffect(() => { fetchData(); fetchFormulas(); }, []);

  useEffect(() => {
    if (loadingAll) timerRef.current = setInterval(() => { setTimer((prev) => prev + 1); }, 1000);
    else { clearInterval(timerRef.current); setTimer(0); }
    return () => clearInterval(timerRef.current);
  }, [loadingAll]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const fetchData = async () => {
    try {
      const fundRes = await axios.get(`${API_BASE_URL}/api/funds`, getAuthHeader());
      setFunds(fundRes.data.funds || []);
      if (fundRes.data.lastUpdated) setLastUpdated(fundRes.data.lastUpdated);
    } catch (error) { console.error('Error fetching data:', error); }
  };

  const fetchFormulas = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/funds/formulas`, getAuthHeader());
      if (res.data) setFormulas(res.data);
    } catch (e) { console.error("Formula Fetch Error:", e); }
    finally { setFormulasLoaded(true); }
  };

  const handleSyncData = async () => {
    setLoadingAll(true); setTimer(0);
    setSnackbar({ open: true, message: 'Running Full Parallel Sync (AUM, Returns)...', severity: 'info' });
    try {
      await axios.post(`${API_BASE_URL}/api/funds/full-sync`, {}, getAuthHeader());
      setSnackbar({ open: true, message: "✅ All Data Synced & Merged Successfully!", severity: 'success' });
      fetchData();
    } catch (error) { setSnackbar({ open: true, message: 'Sync Failed.', severity: 'error' }); }
    setLoadingAll(false);
  };

  const handleFetchTop5 = async () => {
    setLoadingTopFunds(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/funds/top-funds`, { subCategory }, getAuthHeader());
      setTopFundsData(res.data);
      setTopFundsDialogOpen(true);
    } catch (error) { setSnackbar({ open: true, message: 'Failed to fetch Top 5 Funds', severity: 'error' }); }
    setLoadingTopFunds(false);
  };

  const handleOpenUpload = () => setOpenUploadDialog(true);
  const handleCloseUpload = () => { setOpenUploadDialog(false); setSelectedFiles(null); };
  const handleFileChange = (e) => setSelectedFiles(e.target.files);
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleUploadSubmit = async () => {
    if (!selectedFiles) { setSnackbar({ open: true, message: 'Please select files.', severity: 'warning' }); return; }
    setUploading(true);
    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length; i++) formData.append('files', selectedFiles[i]);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/funds/upload-aum`, formData, getUploadHeader());
      setSnackbar({ open: true, message: response.data.message, severity: 'success' });
      handleCloseUpload();
    } catch (error) { setSnackbar({ open: true, message: 'Upload failed.', severity: 'error' }); }
    setUploading(false);
  };

  const handleCloseFormulas = () => {
    if (formRef.current) formRef.current.reset();
    setFormulaDialogOpen(false);
  };

  const handleSaveFormulasClick = (e) => {
    e.preventDefault(); setConfirmFormulaDialog(true);
  };

  const executeSaveFormulas = async () => {
    setConfirmFormulaDialog(false);
    const rawData = Object.fromEntries(new FormData(formRef.current));
    try {
      const res = await axios.post(`${API_BASE_URL}/api/funds/formulas/parse-and-save`, rawData, getAuthHeader());
      setSnackbar({ open: true, message: 'Formulas saved & synced!', severity: 'success' });
      setFormulas(res.data.formulas);
      setFormulaDialogOpen(false);
    } catch (e) { setSnackbar({ open: true, message: 'Failed to save formulas', severity: 'error' }); }
  };

  const handleExcelDownload = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/funds/formulas/download`, { ...getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'Formulas.xlsx');
      document.body.appendChild(link); link.click(); link.remove();
    } catch (error) { console.error("Excel Download Failed", error); }
  };

  const handleFormulaExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData(); formData.append('file', file);
    try {
      setSnackbar({ open: true, message: 'Uploading and updating formulas...', severity: 'info' });
      await axios.post(`${API_BASE_URL}/api/funds/formulas/upload`, formData, getUploadHeader());
      await fetchFormulas();
      setSnackbar({ open: true, message: 'Formulas loaded from Excel successfully!', severity: 'success' });
    } catch (err) { setSnackbar({ open: true, message: 'Excel Upload Failed', severity: 'error' }); }
  };

  const handleCategoryChange = (event, newValue) => { setCategory(newValue); setSubCategory('All'); };
  const handleSubCategoryChange = (event) => { setSubCategory(event.target.value); };
  const handleSearchChange = (event) => { setSearchTerm(event.target.value); };

  const handleViewDetailsClick = (fund) => {
    setSelectedFundDetails(fund); setDetailsDialogOpen(true);
  };

  const renderReturn = (val) => {
    if (!val || val === '-') return '-';
    const num = parseFloat(val);
    return <span style={{ color: num > 15 ? '#204f4f' : num > 0 ? 'black' : '#c62828', fontWeight: 600 }}>{val}%</span>;
  };

  const renderReturnRaw = (val) => {
    if (!val || val === '-') return '-';
    return `${val}%`;
  };

  const uniqueCategories = ['All', 'Equity', 'Hybrid', 'Debt', 'Other', 'Solution Oriented'];
  const subCategoryOptions = [...new Set(funds.filter(f => category === 'All' || f.category === category).map(f => f.subCategory).filter(Boolean))].sort();
  const allGlobalSubCategories = [...new Set(funds.map(f => f.subCategory).filter(Boolean))].sort();

  const getSubCatsForFormula = (cat) => [...new Set(funds.filter(f => f.category === cat).map(f => f.subCategory).filter(Boolean))].sort();

  const subCategoryAverages = useMemo(() => {
    if (!funds || funds.length === 0) return {};
    const averages = {};
    const grouped = {};

    funds.forEach(f => {
      if (!f.subCategory) return;
      if (!grouped[f.subCategory]) grouped[f.subCategory] = [];
      grouped[f.subCategory].push(f);
    });

    const metrics = ['returnYTD', 'return1y', 'return2y', 'return3y', 'return5y', 'return10y'];
    Object.keys(grouped).forEach(subCat => {
      averages[subCat] = {};
      metrics.forEach(metric => {
        const validValues = grouped[subCat].map(f => getVal(f[metric])).filter(v => v !== null);
        if (validValues.length > 0) {
          averages[subCat][metric] = (validValues.reduce((a, b) => a + b, 0) / validValues.length).toFixed(2);
        } else {
          averages[subCat][metric] = '-';
        }
      });
    });
    return averages;
  }, [funds]);

  const filteredFunds = useMemo(() => {
    let result = funds.filter(fund => {
      const lowerSearch = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || (fund.name || '').toLowerCase().includes(lowerSearch) || (fund.code && fund.code.toString().includes(lowerSearch)) || (fund.category || '').toLowerCase().includes(lowerSearch) || (fund.subCategory || '').toLowerCase().includes(lowerSearch);
      const matchesCat = category === 'All' || fund.category === category;
      const matchesSubCat = subCategory === 'All' || fund.subCategory === subCategory;
      return matchesSearch && matchesCat && matchesSubCat;
    });

    Object.keys(colFilters).forEach(field => {
      const filter = colFilters[field];
      if (!filter || (!filter.value && !['is empty', 'is not empty'].includes(filter.operator))) return;
      result = result.filter(r => {
        let cellValue = String(r[field] || '');
        if (field === 'name') cellValue = formatSchemeName(cellValue);
        cellValue = cellValue.toLowerCase();
        const filterVal = String(filter.value || '').toLowerCase();
        switch (filter.operator) {
          case 'contains': return cellValue.includes(filterVal);
          case 'equals': return cellValue === filterVal;
          case 'starts with': return cellValue.startsWith(filterVal);
          case 'ends with': return cellValue.endsWith(filterVal);
          case 'is empty': return cellValue === '' || cellValue === '-';
          case 'is not empty': return cellValue !== '' && cellValue !== '-';
          default: return true;
        }
      });
    });
    return result;
  }, [funds, searchTerm, category, subCategory, colFilters]);


  const getConf = (catKey) => formulas[catKey] || {};

  const renderNativeCell = (catKey, metric, conf) => {
    const metricData = conf[metric] || { op: 'none', val: 0 };
    return (
      <td style={{ padding: '8px 4px', borderBottom: '1px solid #e0e0e0', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'nowrap', gap: '4px' }}>
          <select name={`${catKey}__${metric}__op`} defaultValue={metricData.op} style={{ padding: '6px', border: '1px solid #dcdcdc', borderRadius: '4px', outline: 'none', backgroundColor: '#fafafa', color: '#333' }}>
            <option value="none">-</option><option value=">=">&gt;= Avg</option><option value="<=">&lt;= Avg</option>
          </select>
          <input name={`${catKey}__${metric}__val`} defaultValue={metricData.val} step="0.1" type="number" style={{ width: '50px', padding: '6px', border: '1px solid #dcdcdc', borderRadius: '4px', textAlign: 'center', outline: 'none', backgroundColor: '#fff', color: '#333' }} />
        </div>
      </td>
    );
  };

  // 🔥 Export Excel 
  const handleExportTable = () => {
    const csvContent = [];
    const visibleColsData = colOrder.filter(c => !hiddenColumns.includes(c));
    const headerRow = visibleColsData.map(f => baseColumns.find(c => c.field === f)?.headerName).join(",");
    csvContent.push(headerRow);

    filteredFunds.forEach(row => {
      const rowData = visibleColsData.map(f => {
        let val = row[f] || '-';
        if (f === 'name') val = formatSchemeName(val);
        return `"${val}"`;
      });
      csvContent.push(rowData.join(","));
    });

    const blob = new Blob([csvContent.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.setAttribute("download", "Fund_Selection_Export.csv");
    document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
  };

  // 🔥 Table Setup Logic
  const triggerSaveSettings = (widths, order, frozen, hidden) => {
    localStorage.setItem('fsColWidths', JSON.stringify(widths));
    localStorage.setItem('fsColOrder', JSON.stringify(order));
    localStorage.setItem('fsHiddenCols', JSON.stringify(hidden));
    if (frozen) localStorage.setItem('fsFrozenUntil', frozen);
    else localStorage.removeItem('fsFrozenUntil');
  };

  useEffect(() => {
    let updated = false;
    const newWidths = { ...colWidths };
    baseColumns.forEach(col => {
      if (!newWidths[col.field]) {
        newWidths[col.field] = col.defaultWidth;
        updated = true;
      }
    });
    if (updated) { setColWidths(newWidths); triggerSaveSettings(newWidths, colOrder, frozenUntil, hiddenColumns); }
  }, [baseColumns]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizing) return;
      const delta = e.clientX - resizing.startX;
      const newWidth = Math.max(50, resizing.startWidth + delta);
      setColWidths(prev => ({ ...prev, [resizing.field]: newWidth }));
    };
    const handleMouseUp = () => setResizing(null);
    if (resizing) { window.addEventListener('mousemove', handleMouseMove); window.addEventListener('mouseup', handleMouseUp); }
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [resizing]);

  useEffect(() => {
    if (!resizing && Object.keys(colWidths).length > 0) { triggerSaveSettings(colWidths, colOrder, frozenUntil, hiddenColumns); }
  }, [resizing]);

  const handleResizeStart = (e, field) => { e.preventDefault(); e.stopPropagation(); setResizing({ field, startX: e.clientX, startWidth: colWidths[field] }); };

  const openHeaderMenu = (e, field) => { e.stopPropagation(); setActiveHeaderMenuCol(field); setActiveHeaderElement(e.currentTarget); setHeaderMenuAnchorEl(e.currentTarget); };

  const resetColumnWidth = () => {
    const col = baseColumns.find(c => c.field === activeHeaderMenuCol);
    if (!col) return;
    setColWidths(prev => { const updated = { ...prev, [activeHeaderMenuCol]: col.defaultWidth }; triggerSaveSettings(updated, colOrder, frozenUntil, hiddenColumns); return updated; });
    setHeaderMenuAnchorEl(null);
  };

  const visibleCols = useMemo(() => colOrder.filter(f => !hiddenColumns.includes(f)), [colOrder, hiddenColumns]);

  const frozenOffsets = useMemo(() => {
    const offsets = {};
    if (!frozenUntil) return offsets;
    let currentLeft = 0; let isFrozen = true;
    for (const field of visibleCols) {
      if (isFrozen) {
        offsets[field] = currentLeft;
        currentLeft += (colWidths[field] || baseColumns.find(c => c.field === field)?.defaultWidth || 100);
        if (field === frozenUntil) isFrozen = false;
      }
    }
    return offsets;
  }, [visibleCols, colWidths, frozenUntil, baseColumns]);

  const moveColumn = (direction) => {
    const idx = visibleCols.indexOf(activeHeaderMenuCol);
    if ((direction === -1 && idx > 0) || (direction === 1 && idx < visibleCols.length - 1)) {
      const targetField = visibleCols[idx + direction];
      const realIdx1 = colOrder.indexOf(activeHeaderMenuCol);
      const realIdx2 = colOrder.indexOf(targetField);
      const newOrder = [...colOrder];
      [newOrder[realIdx1], newOrder[realIdx2]] = [newOrder[realIdx2], newOrder[realIdx1]];
      setColOrder(newOrder); triggerSaveSettings(colWidths, newOrder, frozenUntil, hiddenColumns);
    }
    setHeaderMenuAnchorEl(null);
  };

  const openFilter = () => {
    const existing = colFilters[activeHeaderMenuCol] || { operator: 'contains', value: '' };
    setCurrentFilter({ field: activeHeaderMenuCol, ...existing });
    setFilterAnchorEl(activeHeaderElement); setHeaderMenuAnchorEl(null);
  };

  const applyFilter = () => {
    if (currentFilter.value || ['is empty', 'is not empty'].includes(currentFilter.operator)) { setColFilters(prev => ({ ...prev, [currentFilter.field]: { operator: currentFilter.operator, value: currentFilter.value } })); }
    else { const newF = { ...colFilters }; delete newF[currentFilter.field]; setColFilters(newF); }
    setFilterAnchorEl(null);
  };

  const clearFilter = (fieldToClear) => {
    const newF = { ...colFilters }; delete newF[fieldToClear || currentFilter.field]; setColFilters(newF); setFilterAnchorEl(null);
  };

  const handleColumnsClick = (event) => setColAnchorEl(event.currentTarget);
  const handleColumnsClose = () => setColAnchorEl(null);
  const toggleColumn = (field) => {
    let newHidden;
    if (hiddenColumns.includes(field)) newHidden = hiddenColumns.filter(c => c !== field);
    else newHidden = [...hiddenColumns, field];
    setHiddenColumns(newHidden); triggerSaveSettings(colWidths, colOrder, frozenUntil, newHidden);
  };
  const handleHideAll = () => { const allH = baseColumns.map(c => c.field); setHiddenColumns(allH); triggerSaveSettings(colWidths, colOrder, frozenUntil, allH); };
  const handleShowAll = () => { setHiddenColumns([]); triggerSaveSettings(colWidths, colOrder, frozenUntil, []); };
  const filteredColsConfig = baseColumns.filter(c => c.headerName.toLowerCase().includes(colSearch.toLowerCase()));

  const handleDensityClick = (event) => setDensityAnchorEl(event.currentTarget);
  const handleDensityClose = () => setDensityAnchorEl(null);
  const handleDensityChange = (density) => { setTableDensity(density); handleDensityClose(); };
  const getDensityPadding = () => {
    if (tableDensity === 'compact') return '4px 12px';
    if (tableDensity === 'standard') return '10px 16px';
    if (tableDensity === 'comfortable') return '16px 16px';
    return '4px 12px';
  };

  const renderCellContent = (col, row) => {
    if (col.field === 'name') return <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600, color: '#204f4f' }}>{formatSchemeName(row.name)}</Box>;
    if (col.field === 'benchmark') return <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>{row.benchmark || '-'}</Typography>;
    if (col.field === 'category') return <Chip label={row.category} size="small" variant="outlined" sx={{ borderColor: '#204f4f', color: '#204f4f' }} />;
    if (col.field === 'aum') return <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.aum ? `₹${row.aum}` : '-'}</Typography>;
    if (col.field === 'nav') return <Typography variant="body2">₹{row.nav}</Typography>;
    if (['returnYTD', 'return1y', 'return2y', 'return3y', 'return5y', 'return10y', 'returnInception'].includes(col.field)) return renderReturn(row[col.field]);
    return row[col.field];
  };

  const renderHeaderWithAvg = (title, metric) => {
    const avg = subCategory !== 'All' ? subCategoryAverages[subCategory] : null;
    if (!avg || avg[metric] === '-' || avg[metric] === undefined) {
      return <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</Box>;
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', pt: 0.5 }}>
        <span style={{ lineHeight: 1 }}>{title}</span>
        <span style={{ color: '#ffffff', fontSize: '10px', fontWeight: 'normal', opacity: 0.9 }}>Avg: {avg[metric]}</span>
      </Box>
    );
  };

  const baseBtnStyle = {
    bgcolor: '#EED9B7', color: '#000000', border: 'none !important', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)', fontWeight: 'bold', textTransform: 'none', transition: 'all 0.3s ease', whiteSpace: 'nowrap',
    '&:hover': { bgcolor: '#E6C78E', boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)', transform: 'translateY(-2px)' }
  };

  return (
    <Box sx={{ height: { xs: 'calc(100vh - 90px)', md: 'calc(100vh - 100px)' }, overflowY: { xs: 'auto', md: 'hidden' }, display: 'flex', flexDirection: 'column', p: isMobile ? 0 : 2, pb: isMobile ? 2 : 1, maxWidth: '100%', boxSizing: 'border-box' }}>
      <GlobalStyles styles={{ '*::-webkit-scrollbar': { width: '2px', height: '2px' }, '*::-webkit-scrollbar-track': { background: 'transparent' }, '*::-webkit-scrollbar-thumb': { background: '#cfd8d8', borderRadius: '4px' } }} />

      {/* 🚀 HEADER WITH EXACT PLANNER UI */}
      <Box pt={isMobile ? 2 : 0} mb={2} px={isMobile ? 1.5 : 0} sx={{ flexShrink: 0 }}>
        <Typography variant={isMobile ? "h5" : "h4"} sx={{ color: '#0d4745', fontWeight: 600, mb: 1.5 }}>MF Scheme Analyzer</Typography>
        {isMobile && <Divider sx={{ mt: 1, mb: 1.5, borderColor: '#c4a161', borderWidth: 1.5, opacity: 0.6, width: '100%' }} />}

        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} width="100%" justifyContent={isMobile ? 'flex-start' : 'space-between'} alignItems={isMobile ? 'flex-start' : 'center'} gap={isMobile ? 1.5 : 0}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
            {lastUpdated && <Chip icon={<AccessTimeIcon fontSize="small" />} label={`Updated: ${lastUpdated}`} size="small" sx={{ bgcolor: '#e0f2f1', color: '#00695c', fontWeight: 'bold', fontSize: '0.7rem' }} />}
            <Chip icon={<FormatListNumberedIcon fontSize="small" />} label={`Schemes: ${filteredFunds.length}`} size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 'bold', fontSize: '0.7rem' }} />
          </Stack>

          {isMobile ? (
            <Box sx={{ display: 'flex', width: '100%', gap: '4px', m: 0, p: 0 }}>
              <Button onClick={handleOpenUpload} startIcon={<CloudUploadIcon sx={{ fontSize: '1.1rem !important' }} />} sx={{ ...baseBtnStyle, flex: 1, minWidth: 0, px: 0, py: 0.8, fontSize: '0.85rem', fontWeight: 800, color: '#000000', '& .MuiButton-startIcon': { mr: 0.5, ml: 0 } }}>Upload</Button>
              <Button onClick={handleSyncData} disabled={loadingAll} startIcon={loadingAll ? <CircularProgress size={16} color="inherit" /> : <SyncIcon sx={{ fontSize: '1.1rem !important' }} />} sx={{ ...baseBtnStyle, flex: 1, minWidth: 0, px: 0, py: 0.8, fontSize: '0.85rem', fontWeight: 800, color: '#000000', '& .MuiButton-startIcon': { mr: 0.5, ml: 0 } }}>{loadingAll ? 'Syncing' : 'Sync'}</Button>
              {hasTop5Permission && subCategory !== 'All' && <Button onClick={handleFetchTop5} disabled={loadingTopFunds} startIcon={loadingTopFunds ? <CircularProgress size={16} color="inherit" /> : <TrendingUpIcon sx={{ fontSize: '1.1rem !important' }} />} sx={{ ...baseBtnStyle, flex: 1, minWidth: 0, px: 0, py: 0.8, fontSize: '0.85rem', fontWeight: 800, color: '#000000', '& .MuiButton-startIcon': { mr: 0.5, ml: 0 } }}>Top 5</Button>}
              {isSystemOwner && <Button onClick={() => { setFormulaDialogOpen(true); fetchFormulas(); }} startIcon={<SettingsIcon sx={{ fontSize: '1.1rem !important' }} />} sx={{ ...baseBtnStyle, flex: 1, minWidth: 0, px: 0, py: 0.8, fontSize: '0.85rem', fontWeight: 800, color: '#000000', '& .MuiButton-startIcon': { mr: 0.5, ml: 0 } }}>Formulas</Button>}
            </Box>
          ) : (
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button startIcon={<CloudUploadIcon />} onClick={handleOpenUpload} sx={{ ...baseBtnStyle, px: 2 }}>Upload Data</Button>
              <Button startIcon={loadingAll ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />} onClick={handleSyncData} disabled={loadingAll} sx={{ ...baseBtnStyle, px: 2, minWidth: '130px' }}>{loadingAll ? `Syncing... (${formatTime(timer)})` : 'Full Sync'}</Button>
              {hasTop5Permission && subCategory !== 'All' && <Button startIcon={loadingTopFunds ? <CircularProgress size={20} color="inherit" /> : <TrendingUpIcon />} onClick={handleFetchTop5} disabled={loadingTopFunds} sx={{ ...baseBtnStyle, px: 2 }}>Top 5 {subCategory}</Button>}
              {isSystemOwner && <Button startIcon={<SettingsIcon />} onClick={() => { setFormulaDialogOpen(true); fetchFormulas(); }} sx={{ ...baseBtnStyle, px: 2 }}>Formulas</Button>}
            </Box>
          )}
        </Box>
      </Box>

      {/* 🚀 FILTERS & SEARCH */}
      {isMobile ? (
        <Box sx={{ position: 'sticky', top: 0, zIndex: 1100, bgcolor: '#ffffff', px: 1.5, py: 1.5, borderBottom: '1px solid #f0f0f0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <Box display="flex" gap={1}>
            <TextField fullWidth size="small" variant="outlined" placeholder="Search Scheme..." value={searchTerm} onChange={handleSearchChange} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Button variant="contained" onClick={() => setShowFilters(!showFilters)} sx={{ bgcolor: '#204f4f', color: '#ffffff', minWidth: '45px', borderRadius: 2, border: 'none', '&:hover': { bgcolor: '#163838' } }}>
              <FilterListIcon sx={{ color: '#ffffff' }} />
            </Button>
          </Box>
          <Collapse in={showFilters}>
            <Paper elevation={0} sx={{ mt: 1.5, p: 2, borderRadius: 2, border: '1px solid #eee', bgcolor: '#fafafa' }}>
              <Typography variant="caption" fontWeight="bold" color="textSecondary" mb={1} display="block">Category</Typography>
              <Select size="small" fullWidth value={category} onChange={(e) => handleCategoryChange(null, e.target.value)} sx={{ mb: 2, bgcolor: '#ffffff' }}>
                {uniqueCategories.map((cat) => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
              </Select>
              <Typography variant="caption" fontWeight="bold" color="textSecondary" mb={1} display="block">Sub Category</Typography>
              <Select size="small" fullWidth value={subCategory} onChange={handleSubCategoryChange} sx={{ bgcolor: '#ffffff' }}>
                <MenuItem value="All"><em>All</em></MenuItem>
                {subCategoryOptions.map((opt, i) => <MenuItem key={i} value={opt}>{opt}</MenuItem>)}
              </Select>
            </Paper>
          </Collapse>
        </Box>
      ) : (
        <Paper sx={{ flexShrink: 0, mb: 1, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid #eee' }}>
          <Tabs value={category} onChange={handleCategoryChange} variant="scrollable" scrollButtons="auto" sx={{ '& .Mui-selected': { color: '#204f4f' }, '& .MuiTabs-indicator': { backgroundColor: '#204f4f' } }}>{uniqueCategories.map((cat) => (<Tab key={cat} label={cat} value={cat} sx={{ fontWeight: 'bold', textTransform: 'none' }} />))}</Tabs>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', pr: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}><InputLabel>Sub Category</InputLabel><Select value={subCategory} label="Sub Category" onChange={handleSubCategoryChange}><MenuItem value="All"><em>All</em></MenuItem>{subCategoryOptions.map((opt, i) => <MenuItem key={i} value={opt}>{opt}</MenuItem>)}</Select></FormControl>
          </Box>
        </Paper>
      )}

      {/* 🚀 DATA LISTING */}
      {isMobile ? (
        <Box sx={{ mt: 1, bgcolor: '#ffffff', borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', mx: 1.5 }}>
          {filteredFunds.map((fund, index, arr) => (
            <Box key={`mob-watch-${index}`} sx={{ px: 1.2, py: 1.2, borderBottom: index === arr.length - 1 ? 'none' : '1px solid #e0e0e0' }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1c3838', fontSize: '0.90rem', lineHeight: 1.25, pr: 2, flex: 1 }}>{formatSchemeName(fund.name)}</Typography>
                <Box sx={{ textAlign: 'center', minWidth: '55px' }}>
                  <Typography variant="caption" sx={{ color: '#7d9191', fontWeight: 700, fontSize: '0.65rem', display: 'block' }}>10Y Ret</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: getVal(fund.return10y) > 0 ? '#204f4f' : '#c62828' }}>{renderReturnRaw(fund.return10y)}</Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" sx={{ color: '#5498a5', fontWeight: 600, fontSize: '0.70rem', flex: 1, pr: 1 }}>{fund.category} - {fund.subCategory}</Typography>
                <Button size="small" variant="text" sx={{ color: '#c4a161', fontWeight: 'bold', textTransform: 'none', p: 0, minWidth: 'auto', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }} onClick={() => handleViewDetailsClick(fund)}>Analyze More &raquo;</Button>
              </Box>
            </Box>
          ))}
          {filteredFunds.length === 0 && <Typography align="center" color="textSecondary" sx={{ py: 4 }}>No schemes found.</Typography>}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1, minHeight: 0 }}>
          <Paper sx={{ width: '100%', flexGrow: 1, minHeight: 0, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#ffffff', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

            {/* 🔥 Native Toolbar */}
            <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(224, 224, 224, 0.5)', bgcolor: 'rgba(255, 255, 255, 0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box display="flex" gap="15px" alignItems="center" flexWrap="wrap">
                <Button size="small" startIcon={<ViewColumnIcon />} onClick={handleColumnsClick} sx={{ color: '#204f4f', fontWeight: 'bold', textTransform: 'none' }}>Columns</Button>
                <Popover open={Boolean(colAnchorEl)} anchorEl={colAnchorEl} onClose={handleColumnsClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { width: 250, p: 2, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', bgcolor: '#ffffff', backgroundImage: 'none' } }}>
                  <TextField size="small" fullWidth placeholder="Find column" variant="standard" value={colSearch} onChange={(e) => setColSearch(e.target.value)} sx={{ mb: 1 }} />
                  <Box sx={{ maxHeight: 250, overflowY: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#bdbdbd', borderRadius: '4px' } }}>
                    <FormGroup>
                      {filteredColsConfig.map(col => (
                        <FormControlLabel key={col.field} control={<Switch size="small" checked={!hiddenColumns.includes(col.field)} onChange={() => toggleColumn(col.field)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#204f4f' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#204f4f' } }} />} label={<Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>{col.headerName}</Typography>} sx={{ ml: 0, '& .MuiFormControlLabel-label': { ml: 1 } }} />
                      ))}
                    </FormGroup>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mt={1} pt={1} borderTop="1px solid #eee">
                    <Button size="small" onClick={handleHideAll} sx={{ textTransform: 'none', color: '#204f4f', fontWeight: 'bold' }}>Hide all</Button>
                    <Button size="small" onClick={handleShowAll} sx={{ textTransform: 'none', color: '#204f4f', fontWeight: 'bold' }}>Show all</Button>
                  </Box>
                </Popover>

                <Button size="small" startIcon={<DensityMediumIcon />} onClick={handleDensityClick} sx={{ color: '#204f4f', fontWeight: 'bold', textTransform: 'none' }}>Density</Button>
                <Popover open={Boolean(densityAnchorEl)} anchorEl={densityAnchorEl} onClose={handleDensityClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { width: 160, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', bgcolor: '#ffffff', backgroundImage: 'none' } }}>
                  <List disablePadding>
                    <ListItem disablePadding><Button fullWidth onClick={() => handleDensityChange('compact')} sx={{ justifyContent: 'flex-start', px: 2, py: 1, color: tableDensity === 'compact' ? '#204f4f' : '#555', bgcolor: tableDensity === 'compact' ? 'rgba(32, 79, 79, 0.08)' : 'transparent', textTransform: 'none' }}><ListItemIcon sx={{ minWidth: 36 }}><DensitySmallIcon fontSize="small" sx={{ color: tableDensity === 'compact' ? '#204f4f' : '#777' }} /></ListItemIcon><ListItemText primary={<Typography variant="body2" fontWeight={tableDensity === 'compact' ? 'bold' : 'normal'}>Compact</Typography>} /></Button></ListItem>
                    <ListItem disablePadding><Button fullWidth onClick={() => handleDensityChange('standard')} sx={{ justifyContent: 'flex-start', px: 2, py: 1, color: tableDensity === 'standard' ? '#204f4f' : '#555', bgcolor: tableDensity === 'standard' ? 'rgba(32, 79, 79, 0.08)' : 'transparent', textTransform: 'none' }}><ListItemIcon sx={{ minWidth: 36 }}><DensityMediumIcon fontSize="small" sx={{ color: tableDensity === 'standard' ? '#204f4f' : '#777' }} /></ListItemIcon><ListItemText primary={<Typography variant="body2" fontWeight={tableDensity === 'standard' ? 'bold' : 'normal'}>Standard</Typography>} /></Button></ListItem>
                    <ListItem disablePadding><Button fullWidth onClick={() => handleDensityChange('comfortable')} sx={{ justifyContent: 'flex-start', px: 2, py: 1, color: tableDensity === 'comfortable' ? '#204f4f' : '#555', bgcolor: tableDensity === 'comfortable' ? 'rgba(32, 79, 79, 0.08)' : 'transparent', textTransform: 'none' }}><ListItemIcon sx={{ minWidth: 36 }}><DensityLargeIcon fontSize="small" sx={{ color: tableDensity === 'comfortable' ? '#204f4f' : '#777' }} /></ListItemIcon><ListItemText primary={<Typography variant="body2" fontWeight={tableDensity === 'comfortable' ? 'bold' : 'normal'}>Comfortable</Typography>} /></Button></ListItem>
                  </List>
                </Popover>

                <Button size="small" startIcon={<GetAppIcon />} onClick={handleExportTable} sx={{ color: '#204f4f', fontWeight: 'bold', textTransform: 'none' }}>Export Excel</Button>
              </Box>
              <Box display="flex" gap="15px" alignItems="center" flexWrap="wrap">
                <TextField size="small" placeholder="Search Scheme..." variant="standard" value={searchTerm} onChange={handleSearchChange} autoComplete="off" spellCheck="false" InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: '#204f4f', mr: 0.5 }} /></InputAdornment>) }} sx={{ width: '350px', transition: 'width 0.3s ease' }} />
              </Box>
            </Box>

            {/* 🔥 Native Custom Table */}
            <TableContainer sx={{ flexGrow: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#bdbdbd', borderRadius: '10px' } }}>
              <Table stickyHeader size="small" sx={{ minWidth: 1400, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    {visibleCols.map((field) => {
                      const col = baseColumns.find(c => c.field === field);
                      if (!col) return null;
                      const isHovered = hoveredCol === field;
                      const isFrozen = frozenOffsets.hasOwnProperty(field);
                      const leftOffset = isFrozen ? frozenOffsets[field] : 'auto';
                      const isLastFrozen = field === frozenUntil;

                      return (
                        <TableCell key={field} onMouseEnter={() => setHoveredCol(field)} onMouseLeave={() => setHoveredCol(null)}
                          sx={{
                            bgcolor: isFrozen ? '#1a4343' : '#204f4f', color: '#ffffff', fontSize: '0.85rem', fontWeight: 'bold', whiteSpace: 'nowrap',
                            width: colWidths[field] || col.defaultWidth, px: 2, py: 1.5, position: 'sticky', top: 0, left: leftOffset, zIndex: isFrozen ? 5 : 3,
                            boxShadow: isLastFrozen ? '2px 0px 5px -2px rgba(0,0,0,0.5)' : 'none', borderRight: isLastFrozen ? '1px solid #143636' : 'none'
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" sx={{ position: 'relative' }}>
                            {['returnYTD', 'return1y', 'return2y', 'return3y', 'return5y', 'return10y'].includes(field) ? renderHeaderWithAvg(col.headerName, field) : <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.headerName}</Box>}
                            <Box display="flex" alignItems="center" sx={{ position: 'absolute', right: -8, opacity: isHovered || activeHeaderMenuCol === field ? 1 : 0, transition: 'opacity 0.2s', bgcolor: isFrozen ? '#1a4343' : '#204f4f', pl: 0.5 }}>
                              <IconButton size="small" sx={{ color: 'white', p: 0.1 }} onClick={(e) => openHeaderMenu(e, field)}><MoreVertIcon fontSize="small" /></IconButton>
                              <Box onMouseDown={(e) => handleResizeStart(e, field)} sx={{ width: '1px', height: '20px', bgcolor: 'transparent', cursor: 'col-resize', ml: 0.5, '&:hover': { bgcolor: '#ffffff', width: '2px' }, ...(isHovered && { bgcolor: 'rgba(255,255,255,0.5)' }) }} />
                            </Box>
                          </Box>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFunds.map((row, index) => {
                    const cellBgColor = index % 2 === 0 ? '#ffffff' : '#f4f7f7';
                    return (
                      <TableRow key={index} hover sx={{ bgcolor: cellBgColor, '&:hover': { bgcolor: '#e0f2f1' } }}>
                        {visibleCols.map((field) => {
                          const col = baseColumns.find(c => c.field === field);
                          if (!col) return null;
                          const isFrozen = frozenOffsets.hasOwnProperty(field);
                          const leftOffset = isFrozen ? frozenOffsets[field] : 'auto';
                          const isLastFrozen = field === frozenUntil;

                          return (
                            <TableCell key={field} align={col.align || 'left'} sx={{
                              fontSize: '0.85rem', padding: getDensityPadding(), borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              width: colWidths[field] || col.defaultWidth, minWidth: colWidths[field] || col.defaultWidth, maxWidth: colWidths[field] || col.defaultWidth,
                              position: isFrozen ? 'sticky' : 'relative', left: leftOffset, zIndex: isFrozen ? 4 : 1, backgroundColor: cellBgColor,
                              boxShadow: isLastFrozen ? '2px 0px 5px -2px rgba(0,0,0,0.2)' : 'none', borderRight: isLastFrozen ? '1px solid #e0e0e0' : 'none'
                            }}>
                              {renderCellContent(col, row)}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  })}
                  {filteredFunds.length === 0 && (
                    <TableRow><TableCell colSpan={visibleCols.length} align="center" sx={{ py: 3, color: 'text.secondary' }}>No records found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* 🔥 Header Context Menu (Reset Width, Filter, Reorder) */}
            <Menu anchorEl={headerMenuAnchorEl} open={Boolean(headerMenuAnchorEl)} onClose={() => setHeaderMenuAnchorEl(null)} PaperProps={menuPaperProps}>
              <MenuItem onClick={resetColumnWidth} sx={{ fontSize: '0.85rem', color: '#333' }}>
                <SettingsBackupRestoreIcon fontSize="small" sx={{ mr: 1.5, color: '#666' }} /> Reset Width
              </MenuItem>
              <MenuItem onClick={openFilter} sx={{ fontSize: '0.85rem', color: '#333' }}>
                <FilterListIcon fontSize="small" sx={{ mr: 1.5, color: '#666' }} /> Filter Column
              </MenuItem>
              <MenuItem onClick={() => { setFrozenUntil(frozenOffsets.hasOwnProperty(activeHeaderMenuCol) ? null : activeHeaderMenuCol); setHeaderMenuAnchorEl(null); }} sx={{ fontSize: '0.85rem', color: '#333' }}>
                <ViewColumnIcon fontSize="small" sx={{ mr: 1.5, color: '#666' }} /> {frozenOffsets.hasOwnProperty(activeHeaderMenuCol) ? 'Unfreeze Column' : 'Freeze up to here'}
              </MenuItem>
              <Box>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={() => moveColumn(-1)} sx={{ fontSize: '0.85rem', color: '#333' }}><ArrowBackIosNewIcon fontSize="small" sx={{ mr: 1.5, color: '#666' }} /> Move Left</MenuItem>
                <MenuItem onClick={() => moveColumn(1)} sx={{ fontSize: '0.85rem', color: '#333' }}><ArrowForwardIosIcon fontSize="small" sx={{ mr: 1.5, color: '#666' }} /> Move Right</MenuItem>
              </Box>
            </Menu>

            {/* 🔥 Filter Popover for specific column */}
            <Popover open={Boolean(filterAnchorEl)} anchorEl={filterAnchorEl} onClose={() => setFilterAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} sx={{ mt: 1 }} PaperProps={{ sx: { width: 350, p: 2, borderRadius: 2, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)', bgcolor: '#ffffff', backgroundImage: 'none' } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" fontWeight="bold" color="#204f4f">Filter: {baseColumns.find(c => c.field === currentFilter.field)?.headerName}</Typography>
                <IconButton size="small" onClick={() => clearFilter()}><CloseIcon fontSize="small" /></IconButton>
              </Box>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={4}>
                  <Typography variant="caption" color="textSecondary" mb={0.5} display="block">Operator</Typography>
                  <FormControl fullWidth size="small">
                    <Select value={currentFilter.operator} onChange={(e) => setCurrentFilter({ ...currentFilter, operator: e.target.value })} sx={{ fontSize: '0.85rem' }}>
                      <MenuItem value="contains">contains</MenuItem><MenuItem value="equals">equals</MenuItem><MenuItem value="starts with">starts with</MenuItem><MenuItem value="ends with">ends with</MenuItem><MenuItem value="is empty">is empty</MenuItem><MenuItem value="is not empty">is not empty</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="caption" color="textSecondary" mb={0.5} display="block">Value</Typography>
                  <TextField fullWidth size="small" placeholder="Filter value" variant="outlined" value={currentFilter.value} onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })} disabled={['is empty', 'is not empty'].includes(currentFilter.operator)} InputProps={{ sx: { fontSize: '0.85rem' } }} />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
                <Button size="small" onClick={() => clearFilter()} sx={{ color: '#666', textTransform: 'none', fontWeight: 'bold' }}>Clear</Button>
                <Button size="small" variant="contained" onClick={applyFilter} sx={{ bgcolor: '#204f4f', color: '#ffffff', textTransform: 'none', fontWeight: 'bold', '&:hover': { bgcolor: '#153333' } }}>Apply Filter</Button>
              </Box>
            </Popover>

          </Paper>
        </Box>
      )}

      {/* 🌟 VIEW DETAILS POPUP (For Mobile mainly) */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, m: 2, bgcolor: '#f4f7f7', width: 'calc(100% - 32px)', maxHeight: '85vh' } }}>
        {selectedFundDetails && (() => {
          const avg = subCategoryAverages[selectedFundDetails.subCategory] || {};
          return (
            <>
              <DialogTitle sx={{ p: 2, background: 'linear-gradient(90deg, #2a5858, #549090)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.4, fontSize: '0.95rem', pr: 2 }}>{formatSchemeName(selectedFundDetails.name)}</Typography>
                <IconButton onClick={() => setDetailsDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.9)', p: 0 }}><CloseIcon /></IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ p: 2, bgcolor: '#f4f7f7', '&::-webkit-scrollbar': { width: '3px' }, '&::-webkit-scrollbar-track': { background: 'transparent' }, '&::-webkit-scrollbar-thumb': { background: '#cfd8d8', borderRadius: '4px' } }}>
                <Paper elevation={0} sx={{ borderRadius: 2, p: 2, mt: 1, mb: 3, border: '1px solid #e0e6e6' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="caption" color="#7d9191" fontWeight={700} sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Benchmark</Typography>
                    <Typography variant="body2" align="right" color="#1c3838" fontWeight={700} sx={{ fontSize: '0.7rem', maxWidth: '65%', lineHeight: 1.2 }}>{selectedFundDetails.benchmark || '-'}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="caption" color="#7d9191" fontWeight={700} sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>AUM (Cr)</Typography>
                    <Typography variant="body2" align="right" color="#1c3838" fontWeight={800} sx={{ fontSize: '0.7rem' }}>{selectedFundDetails.aum ? `₹${selectedFundDetails.aum}` : '-'}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="caption" color="#7d9191" fontWeight={700} sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>NAV</Typography>
                    <Typography variant="body2" align="right" color="#1c3838" fontWeight={800} sx={{ fontSize: '0.7rem' }}>{selectedFundDetails.nav ? `₹${selectedFundDetails.nav}` : '-'}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="#7d9191" fontWeight={700} sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</Typography>
                    <Typography variant="body2" align="right" color="#1c3838" fontWeight={700} sx={{ fontSize: '0.7rem', maxWidth: '65%', lineHeight: 1.2 }}>{selectedFundDetails.category} / {selectedFundDetails.subCategory}</Typography>
                  </Box>
                </Paper>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#7d9191', letterSpacing: 1, mb: 1, display: 'block', pl: 0.5 }}>RETURNS OVERVIEW</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.5, mb: 1 }}>
                  {[
                    { label: '1Y', val: selectedFundDetails.return1y, avg: avg.return1y },
                    { label: '3Y', val: selectedFundDetails.return3y, avg: avg.return3y },
                    { label: '5Y', val: selectedFundDetails.return5y, avg: avg.return5y },
                    { label: '10Y', val: selectedFundDetails.return10y, avg: avg.return10y }
                  ].map((item, i) => (
                    <Box key={i} sx={{ border: '1px solid #e0e6e6', borderRadius: 2, p: 1.2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#ffffff' }}>
                      <Typography variant="caption" sx={{ color: '#7d9191', fontWeight: 800 }}>{item.label}</Typography>
                      <Box display="flex" alignItems="baseline" gap={0.5}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.85rem', color: getVal(item.val) > 0 ? '#204f4f' : (getVal(item.val) < 0 ? '#c62828' : '#333') }}>{item.val ? `${item.val}%` : '-'}</Typography>
                        <Typography variant="caption" sx={{ color: '#cfd8d8', fontWeight: 500 }}>/</Typography>
                        <Typography variant="caption" sx={{ color: '#7d9191', fontWeight: 700 }}>{item.avg ? `${item.avg}%` : '-'}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </DialogContent>
            </>
          );
        })()}
      </Dialog>

      {/* 🌟 FORMULA ADMIN DIALOG */}
      <Dialog open={formulaDialogOpen} onClose={handleCloseFormulas} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 3, height: isMobile ? '100%' : '92vh', m: isMobile ? 0 : 2, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' } }}>
        <form ref={formRef} onSubmit={handleSaveFormulasClick} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <DialogTitle sx={{ bgcolor: '#1c3838', color: '#ffffff', borderBottom: '3px solid #c4a161', px: { xs: 2, md: 4 }, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <SettingsIcon sx={{ color: '#ffffff', fontSize: '2rem' }} />
              <Typography variant={isMobile ? "subtitle1" : "h5"} fontWeight="bold" fontFamily="'Lato', sans-serif" letterSpacing="0.5px">Formula Configuration</Typography>
            </Box>
            {isMobile && <IconButton onClick={handleCloseFormulas} sx={{ color: 'white' }}><CloseIcon /></IconButton>}
          </DialogTitle>

          <DialogContent sx={{ p: isMobile ? 0 : 0, bgcolor: '#ffffff', overflow: 'auto' }}>
            {formulasLoaded ? (
              <>
                {isMobile ? (
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={2} mb={3} mt={1}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select value={mobFormulaCat} label="Category" onChange={(e) => { setMobFormulaCat(e.target.value); setMobFormulaSubCat(''); }} sx={{ bgcolor: '#f4f7f7' }}>
                          {uniqueCategories.filter(c => c !== 'All').map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth size="small">
                        <InputLabel>Sub Category</InputLabel>
                        <Select value={mobFormulaSubCat} label="Sub Category" onChange={(e) => setMobFormulaSubCat(e.target.value)} sx={{ bgcolor: '#f4f7f7' }}>
                          <MenuItem value=""><em>Select Sub Category</em></MenuItem>
                          {getSubCatsForFormula(mobFormulaCat).map(sc => <MenuItem key={sc} value={sc}>{sc}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Stack>

                    {mobFormulaSubCat && (() => {
                      const catKey = mobFormulaSubCat.toLowerCase().trim();
                      const conf = getConf(catKey);
                      const metrics = [
                        { label: 'AUM', key: 'aum' },
                        { label: '5Y Return', key: 'return5y' },
                        { label: '10Y Return', key: 'return10y' }
                      ];
                      return (
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1, bgcolor: '#fdfbf7' }}>
                          {metrics.map((m, idx) => {
                            const mData = conf[m.key] || { op: 'none', val: 0 };
                            return (
                              <Box key={m.key}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" py={1.5} px={1}>
                                  <Typography variant="body2" fontWeight="bold" color="#1c3838">{m.label}</Typography>
                                  <Box display="flex" gap={1}>
                                    <select name={`${catKey}__${m.key}__op`} defaultValue={mData.op} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px', bgcolor: '#fff' }}>
                                      <option value="none">-</option>
                                      <option value=">=">&gt;= Avg</option>
                                      <option value="<=">&lt;= Avg</option>
                                    </select>
                                    <input name={`${catKey}__${m.key}__val`} defaultValue={mData.val} type="number" step="0.1" style={{ width: '60px', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', textAlign: 'center' }} />
                                  </Box>
                                </Box>
                                <Divider />
                              </Box>
                            );
                          })}
                          <Box display="flex" alignItems="center" justifyContent="space-between" py={1.5} px={1}>
                            <Typography variant="body2" fontWeight="bold" color="#1c3838">Primary Sort By</Typography>
                            <select name={`${catKey}__sortBy`} defaultValue={conf.sortBy || 'return10y'} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontWeight: 'bold', bgcolor: '#fff' }}>
                              <option value="return10y">10Y Return</option>
                              <option value="return5y">5Y Return</option>
                              <option value="aum">AUM</option>
                            </select>
                          </Box>
                        </Box>
                      );
                    })()}
                  </Box>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: 'sans-serif' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f4f1ea', zIndex: 10, boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                      <tr>
                        <th style={{ padding: '16px', borderBottom: '1px solid #dcdcdc', textAlign: 'left', minWidth: '200px', color: '#1c3838', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Sub Category</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #dcdcdc', color: '#1c3838', fontWeight: 'bold' }}>AUM</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #dcdcdc', color: '#1c3838', fontWeight: 'bold' }}>5Y Return</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #dcdcdc', color: '#1c3838', fontWeight: 'bold' }}>10Y Return</th>
                        <th style={{ padding: '16px', borderBottom: '1px solid #dcdcdc', color: '#1c3838', fontWeight: 'bold' }}>Sort By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allGlobalSubCategories.map((subCat, i) => {
                        const catKey = subCat.toLowerCase().trim();
                        const conf = getConf(catKey);
                        return (
                          <tr key={catKey} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa', transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4f1ea'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#ffffff' : '#fafafa'}>
                            <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1c3838', fontSize: '13px' }}>{subCat}</td>
                            {renderNativeCell(catKey, 'aum', conf)}
                            {renderNativeCell(catKey, 'return5y', conf)}
                            {renderNativeCell(catKey, 'return10y', conf)}
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <select name={`${catKey}__sortBy`} defaultValue={conf.sortBy || 'return10y'} style={{ padding: '6px 10px', border: '1px solid #dcdcdc', borderRadius: '4px', outline: 'none', backgroundColor: '#fff', color: '#333', fontWeight: 'bold' }}>
                                <option value="return10y">10Y Return</option>
                                <option value="return5y">5Y Return</option>
                                <option value="aum">AUM</option>
                              </select>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </>
            ) : <Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress sx={{ color: '#c4a161' }} /></Box>}
          </DialogContent>

          <Divider />
          <DialogActions sx={{ p: isMobile ? 2 : 3, bgcolor: '#fdfbf7', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1.5, '& > *': { m: '0 !important' } }}>
            <Box display="flex" gap={1.5} width={isMobile ? '100%' : '50%'}>
              <Button component="label" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', flex: 1, height: '42px', minWidth: isMobile ? 0 : '140px', textTransform: 'none', fontWeight: 'bold', boxShadow: 'none', border: 'none', whiteSpace: 'nowrap', px: 2, transition: 'all 0.3s ease', '&:hover': { bgcolor: '#c8e6c9', boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)', transform: 'translateY(-2px)' } }}>
                Upload Excel
                <input type="file" hidden accept=".xlsx" onChange={handleFormulaExcelUpload} />
              </Button>
              <Button onClick={handleExcelDownload} sx={{ bgcolor: '#e3f2fd', color: '#1565c0', flex: 1, height: '42px', minWidth: isMobile ? 0 : '140px', textTransform: 'none', fontWeight: 'bold', boxShadow: 'none', border: 'none', whiteSpace: 'nowrap', px: 2, transition: 'all 0.3s ease', '&:hover': { bgcolor: '#bbdefb', boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)', transform: 'translateY(-2px)' } }}>
                Download Excel
              </Button>
            </Box>
            <Box display="flex" gap={1.5} width={isMobile ? '100%' : '50%'}>
              <Button onClick={handleCloseFormulas} sx={{ flex: 1, height: '42px', minWidth: isMobile ? 0 : '140px', bgcolor: 'transparent', color: '#555', border: '1px solid #ccc !important', textTransform: 'none', fontWeight: 'bold', boxShadow: 'none', whiteSpace: 'nowrap', px: 2, transition: 'all 0.3s ease', '&:hover': { bgcolor: '#f5f5f5', borderColor: '#aaa', boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)', transform: 'translateY(-2px)' } }}>
                Cancel
              </Button>
              <Button type="submit" sx={{ flex: 1, height: '42px', minWidth: isMobile ? 0 : '140px', bgcolor: '#fdf7e3', color: '#8a6d3b', border: '1px solid #eaddb6 !important', textTransform: 'none', fontWeight: '900', boxShadow: 'none', transition: 'all 0.3s ease', '&:hover': { bgcolor: '#f7ebc6', boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)', transform: 'translateY(-2px)' }, whiteSpace: 'nowrap', px: 2 }}>
                Save Formulas
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>

      {/* 🌟 CONFIRM FORMULA DIALOG */}
      <Dialog open={confirmFormulaDialog} onClose={() => setConfirmFormulaDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#fdf7e3', color: '#8a6d3b', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon /> Confirm Configuration Save
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Typography variant="body1" sx={{ color: '#333' }}>
            Are you sure you want to save these formula configurations?
            <br /><br />
            This will immediately update the sorting and filtering conditions for the <span style={{ fontWeight: 'bold', color: '#8a6d3b' }}>Top 5 Funds</span> calculations across the entire system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#eceff1', justifyContent: 'flex-end' }}>
          <Button onClick={() => setConfirmFormulaDialog(false)} color="inherit" sx={{ fontWeight: 'bold', mr: 1 }}>Cancel</Button>
          <Button onClick={executeSaveFormulas} variant="contained" sx={{ bgcolor: '#8a6d3b', color: 'white', '&:hover': { bgcolor: '#6b542c' } }}>
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🌟 PREMIUM TOP 5 POPUP */}
      <Dialog open={topFundsDialogOpen} onClose={() => setTopFundsDialogOpen(false)} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' } }}>
        <DialogTitle sx={{ bgcolor: '#1c3838', color: '#ffffff', borderBottom: '3px solid #c4a161', px: { xs: 2, sm: 4 }, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <TrendingUpIcon sx={{ color: '#ffffff', fontSize: '2rem' }} />
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" fontFamily="'Lato', sans-serif" letterSpacing="0.5px">Top 5 : <span style={{ color: '#c4a161' }}>{subCategory}</span></Typography>
          </Box>
          <IconButton onClick={() => setTopFundsDialogOpen(false)} sx={{ color: 'white', p: 0 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 1.5 : 0, bgcolor: isMobile ? '#f4f7f7' : '#fff' }}>
          {isMobile ? (
            <Box>
              <Box sx={{ bgcolor: '#ffffff', borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', mt: 1 }}>
                {topFundsData.top5?.map((fund, index) => (
                  <Box key={`top5-mob-${index}`} sx={{
                    px: 1.2, py: 1.2,
                    borderBottom: index === topFundsData.top5.length - 1 ? 'none' : '1px solid #e0e0e0'
                  }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1c3838', fontSize: '0.90rem', lineHeight: 1.25, pr: 2, flex: 1 }}>
                        {formatSchemeName(fund.name)}
                      </Typography>
                      <Box sx={{ textAlign: 'center', minWidth: '55px' }}>
                        <Typography variant="caption" sx={{ color: '#7d9191', fontWeight: 700, fontSize: '0.65rem', display: 'block' }}>10Y Ret</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: getVal(fund.return10y) > 0 ? '#204f4f' : '#c62828' }}>
                          {renderReturnRaw(fund.return10y)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ color: '#5498a5', fontWeight: 600, fontSize: '0.70rem', flex: 1, pr: 1 }}>
                        {fund.category || category} - {fund.subCategory || subCategory}
                      </Typography>
                      <Button size="small" variant="text" sx={{ color: '#c4a161', fontWeight: 'bold', textTransform: 'none', p: 0, minWidth: 'auto', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }} onClick={() => handleViewDetailsClick(fund)}>
                        Analyze More &raquo;
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f4f1ea', color: '#1c3838', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.8rem', py: 2, borderBottom: '1px solid #dcdcdc' } }}>
                    <TableCell sx={{ pl: 4 }}>Scheme Name</TableCell><TableCell>AUM (Cr)</TableCell><TableCell>1Y (%)</TableCell><TableCell>2Y (%)</TableCell>
                    <TableCell>3Y (%)</TableCell><TableCell>5Y (%)</TableCell><TableCell>10Y (%)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topFundsData.averages && (
                    <TableRow sx={{ bgcolor: '#fdfbf7', borderBottom: '2px solid #e0e0e0' }}>
                      <TableCell sx={{ pl: 4, fontWeight: '900', color: '#8a6d3b', fontSize: '0.9rem' }}>{topFundsData.averages.name}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>{topFundsData.averages.aum}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>{topFundsData.averages.return1y}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>{topFundsData.averages.return2y}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>{topFundsData.averages.return3y}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>{topFundsData.averages.return5y}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>{topFundsData.averages.return10y}</TableCell>
                    </TableRow>
                  )}
                  {topFundsData.top5?.map((f, i) => (
                    <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f4f6f8' } }}>
                      <TableCell sx={{ pl: 4 }}><Typography fontWeight={700} color="#1c3838" fontSize="0.95rem">{f.name}</Typography></TableCell>
                      <TableCell sx={{ color: '#444', fontWeight: '500' }}>{f.aum}</TableCell><TableCell>{renderReturn(f.return1y)}</TableCell><TableCell>{renderReturn(f.return2y)}</TableCell>
                      <TableCell>{renderReturn(f.return3y)}</TableCell><TableCell>{renderReturn(f.return5y)}</TableCell><TableCell>{renderReturn(f.return10y)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        {!isMobile && (
          <DialogActions sx={{ p: 2.5, bgcolor: '#fdfbf7' }}>
            <Button onClick={() => setTopFundsDialogOpen(false)} variant="outlined" sx={{ color: '#1c3838', borderColor: '#1c3838', fontWeight: 'bold', textTransform: 'none', px: 4, '&:hover': { bgcolor: '#e0e6e6' } }}>Close</Button>
          </DialogActions>
        )}
      </Dialog>

      {/* 🌟 UPLOAD DIALOG */}
      <Dialog open={openUploadDialog} onClose={handleCloseUpload} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#204f4f', color: 'white', fontWeight: 'bold' }}>Upload AUM Sheets</DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body2" color="textSecondary" align="center">Select all category Excel files to upload and match AUM & Benchmark.</Typography>
            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} sx={{ p: 3, borderStyle: 'dashed', borderWidth: '2px', color: '#204f4f', borderColor: '#204f4f', borderRadius: 2, bgcolor: '#fdfbf7', '&:hover': { bgcolor: '#f4f1ea' } }}>{selectedFiles ? `${selectedFiles.length} Files Selected` : "Click to Choose Excel Files"}<input type="file" hidden multiple accept=".xlsx, .xls, .csv" onChange={handleFileChange} /></Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseUpload} color="inherit" sx={{ fontWeight: 'bold' }}>Cancel</Button>
          <Button onClick={handleUploadSubmit} variant="contained" disabled={uploading || !selectedFiles} startIcon={uploading && <CircularProgress size={20} />} sx={{ bgcolor: '#204f4f', color: 'white', fontWeight: 'bold' }}>{uploading ? 'Uploading...' : 'Upload Data'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontWeight: 'bold' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FundSelector;